import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { leaguesAPI } from '../../../../services/apiService';
import { logSocketEvent, logStateUpdate, startTiming } from '../utils/performanceLogger';

export const useDraftState = (socket, leagueId) => {
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [undoCount, setUndoCount] = useState(0);

  const previousStateRef = useRef(null);
  const isProcessingRef = useRef(false);
  const lastAppliedPickRef = useRef(null); // Track last pick applied via pick-made to skip redundant draft-state
  const lastPickMadeTimestampRef = useRef(0); // Timestamp of last pick-made event - used to skip immediate draft-state

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const handleDraftState = async (state) => {
      // Prevent concurrent processing
      if (isProcessingRef.current) {
        console.warn('[useDraftState] Already processing draft state, skipping');
        return;
      }

      // CRITICAL: Skip draft-state events that arrive shortly after pick-made
      // The server sends both events, but we only need the incremental pick-made update
      // This prevents the "page refresh" feeling caused by full state replacement
      const timeSincePickMade = Date.now() - lastPickMadeTimestampRef.current;
      if (timeSincePickMade < 2000) {
        console.log('[useDraftState] Skipping draft-state received', timeSincePickMade, 'ms after pick-made');
        setLoading(false);
        return;
      }

      // Prevent processing if we're already processing the same state
      // This prevents infinite loops from rapid state updates
      if (previousStateRef.current) {
        const prevStr = JSON.stringify(previousStateRef.current);
        const currStr = JSON.stringify(state);
        if (prevStr === currStr) {
          return; // Skip identical state
        }
      }

      // Secondary check: Skip redundant draft-state if we just applied the same pick via pick-made event
      // This prevents full re-renders and scroll position resets after each pick
      if (lastAppliedPickRef.current && state.currentPick) {
        const lastApplied = lastAppliedPickRef.current;
        // If the incoming state's current pick is the NEXT pick after what we just applied,
        // that means our incremental update was correct - skip the full state replacement
        if (state.currentPick.overall_pick_number === lastApplied.nextPickNumber) {
          console.log('[useDraftState] Skipping redundant draft-state - pick already applied via pick-made');
          lastAppliedPickRef.current = null; // Clear so we accept future draft-state
          setLoading(false);
          return;
        }
      }
      
      isProcessingRef.current = true;
      const endTiming = startTiming('draftState.handleDraftState');
      logSocketEvent('draft-state', { status: state.status, pickCount: state.picks?.length });

      // Fetch draft order from API if picks missing
      if (!state.picks || state.picks.length === 0) {
        try {
          // Use current leagueId from closure (will be latest due to dependency array)
          const orderResponse = await leaguesAPI.getDraftOrder(leagueId);
          if (orderResponse.success) {
            state.picks = orderResponse.draft_picks;
          }
        } catch (err) {
          console.warn('Failed to fetch draft order:', err);
        }
      }

      logStateUpdate('useDraftState', 'draftState', { status: state.status, pickCount: state.picks?.length });
      console.log('[useDraftState] Setting draft state:', {
        status: state.status,
        paused: state.paused,
        currentPickTeam: state.currentPick?.current_owner?.team_name,
        timer: state.timer ? 'present' : 'null'
      });
      // Store state before updating to prevent duplicate processing
      try {
        previousStateRef.current = JSON.parse(JSON.stringify(state));
        setDraftState(state);
        setLoading(false);
      } catch (err) {
        console.error('Error updating draft state:', err);
        // Fallback: just update state without storing previous
        setDraftState(state);
        setLoading(false);
        previousStateRef.current = null;
      } finally {
        isProcessingRef.current = false;
      }
      endTiming({ pickCount: state.picks?.length });
    };

    const handleDraftStarted = (data) => {
      logSocketEvent('draft-started', data);
      logStateUpdate('useDraftState', 'status', 'in_progress');
      // Don't request draft state - server will send it automatically
      // Just optimistically update if we have existing state
      setDraftState(prev => prev ? {
        ...prev,
        status: 'in_progress',
        paused: false,
        ...data
      } : null);
    };

    const handleDraftPaused = () => {
      setDraftState(prev => prev ? {
        ...prev,
        paused: true
      } : prev);
    };

    const handleDraftResumed = () => {
      setDraftState(prev => prev ? {
        ...prev,
        paused: false
      } : prev);
    };

    const handleDraftCompleted = () => {
      setDraftState(prev => prev ? {
        ...prev,
        status: 'completed'
      } : prev);
    };

    const handlePickMade = (data) => {
      logSocketEvent('pick-made', { pickNumber: data.pick_number, playerName: data.player?.info?.full_name });

      // Record timestamp so we can skip the redundant draft-state that follows
      lastPickMadeTimestampRef.current = Date.now();

      // Save scroll position before state update
      const scrollY = window.scrollY;

      // Apply incremental update to avoid full state replacement and scroll reset
      // The server will send draft-state after this, but we'll skip it since we already applied
      setDraftState(prev => {
        if (!prev || !prev.picks) return prev;

        const pickIndex = prev.picks.findIndex(p => p.pick_id === data.pick_id);
        if (pickIndex === -1) {
          console.warn('[useDraftState] pick-made: Could not find pick in state', data.pick_id);
          return prev; // Let draft-state handle it
        }

        // Create updated picks array with the new pick data
        const updatedPicks = [...prev.picks];
        updatedPicks[pickIndex] = {
          ...updatedPicks[pickIndex],
          league_player_id: data.player?.league_player_id,
          salary: data.player?.financial?.baseline_price || data.player?.salary,
          picked_at: data.picked_at,
          picked_by_user_id: data.picked_by,
          roster_status: data.roster_status,
          roster_position: data.roster_position,
          player: {
            league_player_id: data.player?.league_player_id,
            info: {
              full_name: data.player?.info?.full_name,
              position: data.player?.info?.position,
              mlb_team: data.player?.info?.mlb_team
            },
            financial: {
              baseline_price: data.player?.financial?.baseline_price || data.player?.salary
            }
          }
        };

        // Advance to next pick
        const nextPickIndex = pickIndex + 1;
        const nextPick = updatedPicks[nextPickIndex] || null;

        // Track this update so we can skip the redundant draft-state
        lastAppliedPickRef.current = {
          pickId: data.pick_id,
          nextPickNumber: nextPick?.overall_pick_number || null
        };

        console.log('[useDraftState] Applied incremental pick update:', {
          pickNumber: data.pick_number,
          player: data.player?.info?.full_name,
          nextPick: nextPick?.overall_pick_number
        });

        return {
          ...prev,
          picks: updatedPicks,
          currentPick: nextPick,
          currentPickIndex: nextPick ? nextPickIndex : prev.picks.length,
          picksMade: (prev.picksMade || 0) + 1,
          status: nextPick ? prev.status : 'completed'
        };
      });

      // Restore scroll position after React re-render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    };

    const handlePickSkipped = () => {
      // Save scroll position before requesting fresh state
      const scrollY = window.scrollY;
      // Request fresh state - must pass league_id and year
      socket.emit('get-draft-state', { league_id: leagueId, year: draftState?.year });
      // Restore scroll position after re-render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY);
      });
    };

    const handlePickUndone = () => {
      // Request fresh state - must pass league_id and year
      socket.emit('get-draft-state', { league_id: leagueId, year: draftState?.year });
      // Trigger player list re-fetch so undone player reappears
      setUndoCount(c => c + 1);
    };

    const handlePicksTraded = (data) => {
      logSocketEvent('picks-traded', { affectedPicks: data?.affected_picks?.length });
      // Request fresh state after trade - must pass league_id and year
      socket.emit('get-draft-state', { league_id: leagueId, year: draftState?.year });
    };

    const handleError = (data) => {
      setError(data.message);

      // Rollback optimistic update if exists
      if (previousStateRef.current) {
        setDraftState(previousStateRef.current);
        previousStateRef.current = null;
      }
    };

    // Register all handlers
    socket.on('draft-state', handleDraftState);
    socket.on('draft-started', handleDraftStarted);
    socket.on('draft-paused', handleDraftPaused);
    socket.on('draft-resumed', handleDraftResumed);
    socket.on('draft-complete', handleDraftCompleted);
    socket.on('pick-made', handlePickMade);
    socket.on('pick-skipped', handlePickSkipped);
    socket.on('pick-undone', handlePickUndone);
    socket.on('picks-traded', handlePicksTraded);
    socket.on('error', handleError);

    return () => {
      socket.off('draft-state', handleDraftState);
      socket.off('draft-started', handleDraftStarted);
      socket.off('draft-paused', handleDraftPaused);
      socket.off('draft-resumed', handleDraftResumed);
      socket.off('draft-complete', handleDraftCompleted);
      socket.off('pick-made', handlePickMade);
      socket.off('pick-skipped', handlePickSkipped);
      socket.off('pick-undone', handlePickUndone);
      socket.off('picks-traded', handlePicksTraded);
      socket.off('error', handleError);
    };
  }, [socket, leagueId]);

  // Refresh helper
  const refreshDraftState = useCallback(() => {
    if (socket) {
      socket.emit('get-draft-state', { league_id: leagueId, year: draftState?.year });
    }
  }, [socket, leagueId, draftState?.year]);

  // Store previous state for rollback
  const storePreviousState = useCallback(() => {
    previousStateRef.current = JSON.parse(JSON.stringify(draftState));
  }, [draftState]);

  // Computed values
  const isCompleted = useMemo(() =>
    draftState?.status === 'completed', [draftState?.status]);

  const isPaused = useMemo(() =>
    draftState?.paused === true, [draftState?.paused]);

  const isInProgress = useMemo(() =>
    draftState?.status === 'in_progress' && !isPaused,
    [draftState?.status, isPaused]);

  const isWaiting = useMemo(() =>
    draftState?.status === 'waiting', [draftState?.status]);

  const pickCount = useMemo(() =>
    draftState?.picks?.length || 0, [draftState?.picks]);

  const currentRound = useMemo(() => {
    if (!draftState?.currentPick) return 0;
    const totalTeams = new Set(draftState.picks?.map(p => p.original_owner_team_id)).size || 1;
    return Math.ceil(draftState.currentPick.overall_pick_number / totalTeams);
  }, [draftState]);

  // Get set of drafted player IDs for filtering
  // Ref-stabilized: only returns a new Set reference when actual IDs change,
  // preventing downstream re-renders (VirtualAvailablePlayersTable) on unrelated state updates.
  const prevDraftedIdsRef = useRef(new Set());
  const draftedPlayerIds = useMemo(() => {
    const newIds = new Set();
    draftState?.picks?.forEach(pick => {
      if (pick.player_selected?.ids?.league_player) {
        newIds.add(pick.player_selected.ids.league_player);
      }
      if (pick.league_player_id) {
        newIds.add(pick.league_player_id);
      }
    });
    // Only return new reference if contents actually changed
    const prev = prevDraftedIdsRef.current;
    if (newIds.size === prev.size && [...newIds].every(id => prev.has(id))) {
      return prev;
    }
    prevDraftedIdsRef.current = newIds;
    return newIds;
  }, [draftState?.picks]);

  return {
    draftState,
    setDraftState,
    loading,
    setLoading,
    error,
    setError,
    refreshDraftState,
    storePreviousState,
    previousStateRef,
    isCompleted,
    isPaused,
    isInProgress,
    isWaiting,
    pickCount,
    currentRound,
    draftedPlayerIds,
    undoCount
  };
};
